import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sendBlackaMessage } from '../services/chat/blackaChatService';
import { BLACKA_GREETING } from '../config/blackaChat';
import {
  formatBlackaChatErrorReply,
  getSafeBlackaChatErrorMessage,
} from '../utils/blackaChatErrors';
import type { ChatMessage } from '../types/chat';

let messageIdCounter = 0;

function createMessage(role: ChatMessage['role'], content: string): ChatMessage {
  messageIdCounter += 1;
  return {
    id: `blacka-${messageIdCounter}-${Date.now()}`,
    role,
    content,
    createdAt: Date.now(),
  };
}

function extractSupermarketId(pathname: string): string | null {
  const match = pathname.match(/\/supermarkets\/([^/]+)/);
  return match ? match[1] : null;
}

export function useBlackaChat() {
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>([
    createMessage('assistant', BLACKA_GREETING),
  ]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      const userMessage = createMessage('user', trimmed);
      const nextMessages = [...messagesRef.current, userMessage];
      setMessages(nextMessages);
      setLoading(true);
      setError('');

      try {
        const response = await sendBlackaMessage({
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
          pagePath: location.pathname,
          supermarketId: extractSupermarketId(location.pathname),
        });

        setMessages((prev) => [...prev, createMessage('assistant', response.reply)]);
      } catch (err: unknown) {
        const message = getSafeBlackaChatErrorMessage(err);
        setError(message);
        setMessages((prev) => [
          ...prev,
          createMessage('assistant', formatBlackaChatErrorReply(message)),
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, location.pathname]
  );

  const clearChat = useCallback(() => {
    setMessages([createMessage('assistant', BLACKA_GREETING)]);
    setError('');
  }, []);

  return {
    messages,
    loading,
    error,
    sendMessage,
    clearChat,
  };
}
